import { fitWithin } from './logo';
import { redraw } from './profilePhoto';

/**
 * A picture a guardian sends in chat (Q5, Q46), shrunk and re-encoded in the browser before upload.
 *
 * Kept whole, as a logo is: a prescription cut to its centre is no longer readable. Re-encoding through a
 * canvas is also what strips the photo's metadata, its location included, because a canvas holds pixels
 * only. The server has no image library, so this is the only place either happens.
 */

/** JPEG quality: a prescription's small print stays legible, and a phone photo leaves at about a megabyte. */
const JPEG_QUALITY = 0.85;

/** JPEG has no transparency, so a transparent PNG is flattened onto white rather than black. */
const BACKGROUND = '#ffffff';

/**
 * Decodes an image file and re-encodes it as a JPEG no larger than `maxEdge` on its longest side:
 * `chat.imageMaximumEdge` from `useLimits`.
 *
 * @throws UnreadablePhotoError when the browser cannot decode the file (HEIC in Chrome, a PDF, a broken file).
 */
export function toChatImage(file: Blob, maxEdge: number): Promise<Blob> {
  return redraw(file, (image) => {
    const size = fitWithin(image.naturalWidth, image.naturalHeight, maxEdge);
    return {
      ...size,
      draw: (context) => {
        context.fillStyle = BACKGROUND;
        context.fillRect(0, 0, size.width, size.height);
        context.drawImage(image, 0, 0, size.width, size.height);
      },
    };
  }, 'image/jpeg', JPEG_QUALITY);
}
